/**
 * useTelemetryPipeline.ts
 * Feature: 155 - Telemetry Pipeline Provisioning & Management
 * Author: Peter Dobler (Dobler Data Solutions)
 * Date: 2026-05-19
 * Constitution Version: v1.12.0 | Principle XXXVII (Code Header Standards)
 *
 * React Query hooks for telemetry pipeline management and insights.
 * Management hooks auto-refetch every 30 seconds (TC-007 freshness SLA).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/apiClient';
import type {
  SmaListResponse,
  SmaDetail,
  SlugPreviewResponse,
  InsightsResponse,
  InsightsHistoryResponse,
  MoveCapacitySmaRequest,
  MoveCapacitySmaResponse,
} from '@shared/types/telemetryPipeline';

const BASE = '/api/telemetry-pipelines';

async function apiFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase();
  const body = init?.body;
  const response = await apiClient.request<T>({
    url,
    method,
    data: body ? JSON.parse(body as string) : undefined,
  });
  return response.data;
}

// ── Assigned capacity IDs (for picker exclusion) ──────────────────────────────

export function useAssignedCapacityIds(): Set<string> {
  const { data } = useQuery<{ assignedCapacityIds: string[] }>({
    queryKey: ['assigned-capacity-ids'],
    queryFn: () => apiFetch<{ assignedCapacityIds: string[] }>(`${BASE}/assigned-capacity-ids`),
    refetchInterval: 30_000,
  });
  return new Set(data?.assignedCapacityIds ?? []);
}

// ── Management: SMA list ──────────────────────────────────────────────────────

export function useSubjectMatterAreaList() {
  return useQuery<SmaListResponse>({
    queryKey: ['smas'],
    queryFn: () => apiFetch<SmaListResponse>(`${BASE}/subject-matter-areas`),
    refetchInterval: 30_000,
  });
}

// ── Management: SMA detail ────────────────────────────────────────────────────

export function useSubjectMatterAreaDetail(slug: string) {
  return useQuery<SmaDetail>({
    queryKey: ['sma-detail', slug],
    queryFn: () => apiFetch<SmaDetail>(`${BASE}/subject-matter-areas/${slug}`),
    refetchInterval: 30_000,
    enabled: !!slug,
  });
}

// ── Mutations: move capacity ──────────────────────────────────────────────────

export function useMoveCapacity() {
  const qc = useQueryClient();
  return useMutation<
    MoveCapacitySmaResponse,
    Error,
    { capacityId: string; body: MoveCapacitySmaRequest }
  >({
    mutationFn: ({ capacityId, body }) =>
      apiFetch<MoveCapacitySmaResponse>(
        `${BASE}/capacities/${capacityId}/subject-matter-area`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['smas'] });
    },
  });
}

// ── Mutations: teardown SMA ───────────────────────────────────────────────────

export function useTeardownSma() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { slug: string }>({
    mutationFn: ({ slug }) =>
      apiFetch<unknown>(`${BASE}/subject-matter-areas/${slug}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['smas'] });
    },
  });
}

// ── Slug preview (with enabled guard) ────────────────────────────────────────

export function useSlugPreview(displayName: string) {
  return useQuery<SlugPreviewResponse>({
    queryKey: ['slug-preview', displayName],
    queryFn: () =>
      apiFetch<SlugPreviewResponse>(`${BASE}/slug-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayName }),
      }),
    enabled: displayName.trim().length > 1,
    staleTime: 0,
  });
}

// ── Insights ──────────────────────────────────────────────────────────────────

export function useInsights(from: string, to: string) {
  return useQuery<InsightsResponse>({
    queryKey: ['insights', from, to],
    queryFn: () =>
      apiFetch<InsightsResponse>(`${BASE}/insights?from=${from}&to=${to}`),
  });
}

export function useInsightsHistory(
  smaSlug: string,
  from: string,
  to: string,
  granularity: 'hour' | 'day' = 'hour',
) {
  return useQuery<InsightsHistoryResponse>({
    queryKey: ['insights-history', smaSlug, from, to, granularity],
    queryFn: () =>
      apiFetch<InsightsHistoryResponse>(
        `${BASE}/insights/history/${smaSlug}?from=${from}&to=${to}&granularity=${granularity}`,
      ),
    enabled: !!smaSlug,
  });
}
